/**
 * Workflow Definition Detail Page
 *
 * Visualizes a workflow definition's state machine as an interactive diagram.
 * Right panel shows live running instances — click to highlight the current state.
 * All data from backend via useGetWorkflowQuery — dumb frontend pattern (DRR-055).
 *
 * Route: /ferp-core/workflows/:workflowId
 *
 * RBAC Scopes Required:
 * - workflow:read - View workflow definitions
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Container,
  Stack,
  Group,
  Title,
  Text,
  Badge,
  Card,
  Alert,
  Loader,
  Drawer,
  Divider,
  List,
  Paper,
  Breadcrumbs,
  Anchor,
  ActionIcon,
  Tooltip,
  Progress,
  Box,
  ScrollArea,
  ThemeIcon,
  Center,
  Modal,
  TextInput,
  Button,
  Switch,
  Select,
  SegmentedControl,
  Collapse,
} from '@mantine/core';
import { OrchestrationGraph } from '@/pages/subgraphs/components/OrchestrationGraph';
import { useDisclosure } from '@mantine/hooks';
import {
  IconArrowLeft,
  IconAlertTriangle,
  IconClock,
  IconGitBranch,
  IconShield,
  IconPlayerPlay,
  IconCircleCheck,
  IconCircleX,
  IconChevronRight,
  IconChevronLeft,
  IconChevronDown,
  IconStack2,
  IconRocket,
  IconEye,
  IconSearch,
  IconRoute,
} from '@tabler/icons-react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  useGetWorkflowQuery,
  useGetInstanceQuery,
  useListInstancesByWorkflowQuery,
  useListWorkflowsQuery,
  useCreateInstanceMutation,
  useStartInstanceMutation,
  GqlStateType,
  GqlInstanceStatus,
  type GetWorkflowQuery,
  type ListInstancesByWorkflowQuery,
} from '@/generated/graphql';
import { useTenantId } from '@/auth';
import {
  ReactFlowCanvas,
  type FlowNode,
  type FlowEdge,
  type BaseNodeData,
  type BaseEdgeData,
  type CanvasConfig,
} from '@ferp/workflow-forms';

// ============================================================================
// State type → color mapping (uses backend GqlStateType enum, no hardcoded strings)
// ============================================================================

const STATE_TYPE_COLORS: Record<string, string> = {
  [GqlStateType.INITIAL]: '#3b82f6',
  [GqlStateType.NORMAL]: '#6b7280',
  [GqlStateType.FINAL]: '#10b981',
  [GqlStateType.CHOICE]: '#f59e0b',
  [GqlStateType.PARALLEL]: '#8b5cf6',
};

const STATE_TYPE_LABELS: Record<string, string> = {
  [GqlStateType.INITIAL]: 'Initial',
  [GqlStateType.NORMAL]: 'Intermediate',
  [GqlStateType.FINAL]: 'Final',
  [GqlStateType.CHOICE]: 'Decision',
  [GqlStateType.PARALLEL]: 'Parallel',
};

const INSTANCE_STATUS_COLORS: Record<string, string> = {
  [GqlInstanceStatus.RUNNING]: 'blue',
  [GqlInstanceStatus.COMPLETED]: 'green',
  [GqlInstanceStatus.FAILED]: 'red',
  [GqlInstanceStatus.PAUSED]: 'orange',
  [GqlInstanceStatus.PENDING]: 'yellow',
  [GqlInstanceStatus.CANCELLED]: 'gray',
  [GqlInstanceStatus.TIMED_OUT]: 'red',
  [GqlInstanceStatus.COMPENSATING]: 'violet',
  [GqlInstanceStatus.COMPENSATED]: 'grape',
};

// ============================================================================
// State machine → ReactFlow conversion
// ============================================================================

type WorkflowData = NonNullable<GetWorkflowQuery['getWorkflow']>;
type InstanceItem = ListInstancesByWorkflowQuery['listInstancesByWorkflow']['items'][number];

function formatStateName(name: string): string {
  return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatSeconds(seconds: number): string {
  if (seconds >= 86400) return `${Math.round(seconds / 86400)}d`;
  if (seconds >= 3600) return `${Math.round(seconds / 3600)}h`;
  return `${Math.round(seconds / 60)}m`;
}

function convertStateMachineToFlow(
  workflow: WorkflowData,
  highlightedStateId?: string | null,
): {
  nodes: FlowNode<BaseNodeData>[];
  edges: FlowEdge<BaseEdgeData>[];
} {
  const { states, transitions, guards } = workflow;

  const guardMap = new Map(guards.map((g) => [g.name, g.expression.expression]));

  // Count outgoing transitions per state for terminal detection
  const outgoingCount = new Map<string, number>();
  for (const t of transitions) {
    outgoingCount.set(t.fromState, (outgoingCount.get(t.fromState) ?? 0) + 1);
  }

  // Auto-layout: separate main path from terminal/branching states
  const initialState = states.find((s) => s.stateType === GqlStateType.INITIAL);
  const terminalStates = states.filter(
    (s) => !outgoingCount.has(s.name) && s.stateType !== GqlStateType.INITIAL,
  );
  const terminalNames = new Set(terminalStates.map((s) => s.name));
  const mainPathStates = states.filter(
    (s) => s.stateType === GqlStateType.INITIAL || !terminalNames.has(s.name),
  );

  // Order main path by transition chain
  const ordered: string[] = [];
  const visited = new Set<string>();
  if (initialState) {
    const queue = [initialState.name];
    while (queue.length > 0) {
      const current = queue.shift()!;
      if (visited.has(current) || terminalNames.has(current)) continue;
      visited.add(current);
      ordered.push(current);
      const outgoing = transitions
        .filter((t) => t.fromState === current && !terminalNames.has(t.toState))
        .map((t) => t.toState);
      queue.push(...outgoing);
    }
  }
  // Add any unvisited non-terminal states
  for (const s of mainPathStates) {
    if (!visited.has(s.name)) ordered.push(s.name);
  }

  const nodeWidth = 260;
  const nodeHeight = 100;
  const verticalGap = 140;
  const horizontalGap = 320;

  const statePositions = new Map<string, { x: number; y: number }>();
  ordered.forEach((name, i) => {
    statePositions.set(name, { x: 200, y: i * verticalGap });
  });
  // Terminal states offset to the right
  terminalStates.forEach((s, i) => {
    const sourceTransition = transitions.find((t) => t.toState === s.name);
    const sourceY = sourceTransition
      ? statePositions.get(sourceTransition.fromState)?.y ?? i * verticalGap
      : i * verticalGap;
    statePositions.set(s.name, { x: 200 + horizontalGap, y: sourceY });
  });

  const nodes: FlowNode<BaseNodeData>[] = states.map((state) => {
    const hasNoOutgoing = !outgoingCount.has(state.name);
    const isDeclineTerminal =
      hasNoOutgoing &&
      state.stateType !== GqlStateType.INITIAL &&
      (state.name.includes('declined') ||
        state.name.includes('rejected') ||
        state.name.includes('failed'));

    let nodeColor = STATE_TYPE_COLORS[state.stateType] ?? '#6b7280';
    if (isDeclineTerminal) nodeColor = '#ef4444';

    // Highlight the current state of the selected instance
    // policyRefs available after codegen (backend exposes via Gap #7)
    const statePolicyRefs: string[] = (state as Record<string, unknown>).policyRefs as string[] ?? [];

    const isHighlighted = highlightedStateId === state.name;
    if (isHighlighted) nodeColor = '#f97316'; // amber-orange "you are here"

    const pos = statePositions.get(state.name) ?? { x: 0, y: 0 };

    return {
      id: state.name,
      type: 'default',
      position: { x: pos.x - nodeWidth / 2, y: pos.y - nodeHeight / 2 },
      style: isHighlighted
        ? { boxShadow: '0 0 0 3px #f97316, 0 0 12px rgba(249,115,22,0.4)' }
        : undefined,
      data: {
        id: state.name,
        type: 'WorkflowDefinition' as const,
        label: formatStateName(state.name),
        description: isHighlighted
          ? '▶ Current state'
          : [
              STATE_TYPE_LABELS[state.stateType] ?? state.stateType,
              state.timeoutSeconds ? formatSeconds(state.timeoutSeconds) : null,
              statePolicyRefs.length > 0 ? `🛡 ${statePolicyRefs.length} policy gate${statePolicyRefs.length > 1 ? 's' : ''}` : null,
            ].filter(Boolean).join(' · '),
        metadata: {
          stateType: state.stateType,
          timeoutSeconds: state.timeoutSeconds,
          entryActions: state.entryActions,
          exitActions: state.exitActions,
          policyRefs: statePolicyRefs,
          rawName: state.name,
        },
        renderConfig: {
          color: nodeColor,
          badge: isHighlighted
            ? 'Current'
            : (STATE_TYPE_LABELS[state.stateType] ?? state.stateType),
        },
      },
    };
  });

  const edges: FlowEdge<BaseEdgeData>[] = transitions.map((transition, index) => {
    const guardLabels = transition.guards.map((g) => `[${g}]`).join(' ');
    const edgeLabel = guardLabels
      ? `${transition.event}\n${guardLabels}`
      : transition.event;

    return {
      id: `${transition.fromState}-${transition.event}-${transition.toState}`,
      source: transition.fromState,
      target: transition.toState,
      label: edgeLabel,
      type: 'smoothstep',
      style: {
        stroke: terminalNames.has(transition.toState) ? '#ef4444' : '#9ca3af',
      },
      data: {
        id: `transition-${index}`,
        label: transition.event,
        condition: transition.guards.join(', ') || undefined,
        metadata: {
          event: transition.event,
          guards: transition.guards,
          actions: transition.actions,
          guardExpressions: transition.guards.map((g) => ({
            name: g,
            expression: guardMap.get(g),
          })),
        },
      },
    };
  });

  return { nodes, edges };
}

// ============================================================================
// Instance status icon helper
// ============================================================================

function InstanceStatusIcon({ status }: { status: string }) {
  if (status === GqlInstanceStatus.COMPLETED) return <IconCircleCheck size={14} />;
  if (status === GqlInstanceStatus.FAILED || status === GqlInstanceStatus.TIMED_OUT)
    return <IconCircleX size={14} />;
  if (status === GqlInstanceStatus.RUNNING) return <IconPlayerPlay size={14} />;
  return <IconClock size={14} />;
}

// ============================================================================
// Instances Panel
// ============================================================================

interface InstancesPanelProps {
  workflowId: string;
  tenantId: string;
  selectedInstanceId: string | null;
  onInstanceSelect: (instance: InstanceItem) => void;
}

function InstancesPanel({
  workflowId,
  tenantId,
  selectedInstanceId,
  onInstanceSelect,
}: InstancesPanelProps) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'running'>('all');

  const { data, loading, error } = useListInstancesByWorkflowQuery({
    variables: { workflowDefinitionId: workflowId, tenantId, limit: 50 },
    pollInterval: 10_000,
  });

  const instances = data?.listInstancesByWorkflow.items ?? [];
  const total = data?.listInstancesByWorkflow.total ?? 0;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return instances.filter((inst) => {
      if (statusFilter === 'running' && inst.status !== GqlInstanceStatus.RUNNING) return false;
      if (!q) return true;
      return (
        (inst.instanceName ?? '').toLowerCase().includes(q) ||
        inst.id.toLowerCase().includes(q) ||
        inst.currentState.toLowerCase().includes(q) ||
        inst.status.toLowerCase().includes(q)
      );
    });
  }, [instances, search, statusFilter]);

  if (loading && instances.length === 0) {
    return (
      <Center py="xl">
        <Stack align="center" gap="xs">
          <Loader size="sm" />
          <Text size="xs" c="dimmed">Loading instances...</Text>
        </Stack>
      </Center>
    );
  }

  if (error) {
    return (
      <Alert color="red" p="xs" title="Error">
        <Text size="xs">{error.message}</Text>
      </Alert>
    );
  }

  return (
    <Stack gap="xs">
      {/* Search + filter controls */}
      <TextInput
        size="xs"
        placeholder="Search by name, state, status..."
        leftSection={<IconSearch size={12} />}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
      />
      <SegmentedControl
        size="xs"
        fullWidth
        value={statusFilter}
        onChange={(v) => setStatusFilter(v as 'all' | 'running')}
        data={[
          { label: 'All', value: 'all' },
          { label: 'Running', value: 'running' },
        ]}
      />
      <Text size="xs" c="dimmed">
        {filtered.length} of {total} run{total !== 1 ? 's' : ''}
      </Text>

      {filtered.length === 0 && instances.length === 0 && (
        <Center py="md">
          <Stack align="center" gap="xs">
            <ThemeIcon size="lg" variant="light" color="gray">
              <IconStack2 size={20} />
            </ThemeIcon>
            <Text size="xs" c="dimmed" ta="center">
              No instances yet.
              <br />Start a workflow to see runs here.
            </Text>
          </Stack>
        </Center>
      )}

      {filtered.length === 0 && instances.length > 0 && (
        <Center py="md">
          <Text size="xs" c="dimmed">No matches for "{search}"</Text>
        </Center>
      )}

      {filtered.map((instance) => {
        const isSelected = instance.id === selectedInstanceId;
        const statusColor = INSTANCE_STATUS_COLORS[instance.status] ?? 'gray';
        return (
          <Paper
            key={instance.id}
            p="xs"
            withBorder
            radius="sm"
            style={{
              cursor: 'pointer',
              borderColor: isSelected ? 'var(--mantine-color-orange-6)' : undefined,
              backgroundColor: isSelected ? 'var(--mantine-color-orange-0)' : undefined,
            }}
            onClick={() => onInstanceSelect(instance)}
          >
            <Stack gap={4}>
              <Group justify="space-between" gap="xs" wrap="nowrap">
                <Text size="xs" fw={500} truncate style={{ flex: 1 }}>
                  {instance.instanceName ?? instance.id.slice(-8)}
                </Text>
                <Badge
                  size="xs"
                  variant="light"
                  color={statusColor}
                  leftSection={<InstanceStatusIcon status={instance.status} />}
                >
                  {instance.status}
                </Badge>
              </Group>
              {instance.status === GqlInstanceStatus.RUNNING && (
                <Group gap="xs" align="center">
                  <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                    {instance.completedTasks}/{instance.totalTasks}
                  </Text>
                  <Progress
                    value={instance.progressPercent}
                    size="xs"
                    color="blue"
                    style={{ flex: 1 }}
                  />
                </Group>
              )}
              <Text size="xs" c="dimmed">
                → {formatStateName(instance.currentState)}
              </Text>
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}

// ============================================================================
// Component
// ============================================================================

interface SelectedStateInfo {
  name: string;
  stateType: string;
  timeoutSeconds: number | null;
  entryActions: string[];
  exitActions: string[];
  policyRefs: string[];
  incomingTransitions: WorkflowData['transitions'];
  outgoingTransitions: WorkflowData['transitions'];
}

export function WorkflowDetailPage(): React.ReactElement {
  const { workflowId } = useParams<{ workflowId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const focusInstanceId = searchParams.get('instance') ?? undefined;
  const tenantId = useTenantId() ?? '';
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);
  const [launchOpened, { open: openLaunch, close: closeLaunch }] = useDisclosure(false);
  const [selectedState, setSelectedState] = useState<SelectedStateInfo | null>(null);
  const [selectedInstance, setSelectedInstance] = useState<InstanceItem | null>(null);
  const autoSelectedRef = useRef(false);
  const [instancesPanelOpen, setInstancesPanelOpen] = useState(true);
  const [launchName, setLaunchName] = useState('');
  const [autoStart, setAutoStart] = useState(true);
  const [launchError, setLaunchError] = useState<string | null>(null);
  const [launchLoading, setLaunchLoading] = useState(false);
  const [orchestrationOpen, setOrchestrationOpen] = useState(false);

  const [createInstance] = useCreateInstanceMutation();
  const [startInstance] = useStartInstanceMutation();

  // Workflow switcher — load all workflow names for the jump-to select
  const { data: workflowsData } = useListWorkflowsQuery({ variables: { limit: 200 } });
  const workflowOptions = useMemo(
    () =>
      (workflowsData?.listWorkflows.items ?? []).map((wf) => ({
        value: wf.id,
        label: formatStateName(wf.name),
      })),
    [workflowsData],
  );

  const { data, loading, error } = useGetWorkflowQuery({
    variables: { workflowId: workflowId ?? '' },
    skip: !workflowId,
  });

  // When launched from WorkflowLaunchModal with ?instance=<id>, auto-select that instance
  // so its current state is immediately highlighted on the DAG.
  const { data: focusData } = useGetInstanceQuery({
    variables: { tenantId, instanceId: focusInstanceId ?? '' },
    skip: !focusInstanceId || !tenantId,
    pollInterval: 3_000,
  });
  useEffect(() => {
    if (autoSelectedRef.current) return;
    const inst = focusData?.getInstance;
    if (!inst) return;
    autoSelectedRef.current = true;
    setSelectedInstance(inst as InstanceItem);
    setInstancesPanelOpen(true);
  }, [focusData]);

  const workflow = data?.getWorkflow;

  const { nodes, edges } = useMemo(() => {
    if (!workflow) return { nodes: [], edges: [] };
    return convertStateMachineToFlow(workflow, selectedInstance?.currentState);
  }, [workflow, selectedInstance]);

  const handleNodeClick = useCallback(
    (nodeId: string, _nodeData: BaseNodeData) => {
      if (!workflow) return;
      const state = workflow.states.find((s) => s.name === nodeId);
      if (!state) return;

      setSelectedState({
        name: state.name,
        stateType: state.stateType,
        timeoutSeconds: state.timeoutSeconds ?? null,
        entryActions: state.entryActions,
        exitActions: state.exitActions,
        policyRefs: (state as Record<string, unknown>).policyRefs as string[] ?? [],
        incomingTransitions: workflow.transitions.filter((t) => t.toState === state.name),
        outgoingTransitions: workflow.transitions.filter((t) => t.fromState === state.name),
      });
      openDrawer();
    },
    [workflow, openDrawer],
  );

  const handleLaunch = useCallback(async () => {
    if (!workflowId || !tenantId) return;
    setLaunchError(null);
    setLaunchLoading(true);
    try {
      const { data } = await createInstance({
        variables: {
          tenantId,
          input: {
            workflowDefinitionId: workflowId,
            instanceName: launchName.trim() || undefined,
            inputData: {},
          },
        },
      });
      const newInstance = data?.createInstance;
      if (newInstance && autoStart) {
        await startInstance({
          variables: { tenantId, instanceId: newInstance.id, workflowDefinitionId: workflowId },
        });
      }
      closeLaunch();
      setLaunchName('');
      if (newInstance) {
        // Stay on this page — select the new instance so its current state is highlighted on the DAG.
        setSelectedInstance(newInstance as InstanceItem);
        setInstancesPanelOpen(true);
      }
    } catch (e: unknown) {
      setLaunchError(e instanceof Error ? e.message : String(e));
    } finally {
      setLaunchLoading(false);
    }
  }, [workflowId, tenantId, launchName, autoStart, createInstance, startInstance, closeLaunch]);

  const canvasConfig: CanvasConfig = useMemo(
    () => ({
      showMiniMap: true,
      showControls: true,
      showBackground: true,
      fitView: true,
      nodesDraggable: true,
      onNodeClick: handleNodeClick,
    }),
    [handleNodeClick],
  );

  // Loading
  if (loading) {
    return (
      <Container size="lg" py="xl">
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text>Loading workflow definition...</Text>
        </Stack>
      </Container>
    );
  }

  // Error
  if (error) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} title="Error Loading Workflow" color="red">
          {error.message}
        </Alert>
      </Container>
    );
  }

  // Not found
  if (!workflow) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} title="Workflow Not Found" color="yellow">
          No workflow definition found for ID: {workflowId}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Breadcrumbs */}
        <Breadcrumbs>
          <Anchor onClick={() => navigate('/ferp-core/workflows')}>Workflows</Anchor>
          <Text>{workflow.name}</Text>
        </Breadcrumbs>

        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <Group gap="md" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
            <Tooltip label="Back to Workflows">
              <ActionIcon variant="subtle" onClick={() => navigate('/ferp-core/workflows')}>
                <IconArrowLeft size={20} />
              </ActionIcon>
            </Tooltip>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Group gap="sm" align="center" wrap="nowrap">
                <Title order={2} style={{ whiteSpace: 'nowrap' }}>
                  {formatStateName(workflow.name)}
                </Title>
                {workflowOptions.length > 1 && (
                  <Select
                    size="xs"
                    placeholder="Jump to workflow..."
                    leftSection={<IconSearch size={12} />}
                    data={workflowOptions}
                    value={workflowId ?? null}
                    searchable
                    clearable={false}
                    style={{ width: 220 }}
                    onChange={(id) => {
                      if (id && id !== workflowId) navigate(`/ferp-core/workflows/${id}`);
                    }}
                  />
                )}
              </Group>
              {workflow.description && (
                <Text c="dimmed" size="sm" mt={4}>
                  {workflow.description}
                </Text>
              )}
            </div>
          </Group>
          <Group gap="xs">
            <Badge variant="light" color="blue" leftSection={<IconGitBranch size={12} />}>
              v{workflow.version}
            </Badge>
            <Badge variant="light" color={workflow.active ? 'green' : 'gray'}>
              {workflow.active ? 'Active' : 'Inactive'}
            </Badge>
            {workflow.executionStrategy && (
              <Badge variant="outline">{workflow.executionStrategy}</Badge>
            )}
            <Badge
              variant="light"
              color={workflow.criticality === 'life_critical' ? 'red' : 'gray'}
              leftSection={<IconShield size={12} />}
            >
              {workflow.criticality}
            </Badge>
            <Tooltip label="View live instances in Observer (PII-free)">
              <Button
                size="xs"
                variant="light"
                color="violet"
                leftSection={<IconEye size={14} />}
                component={Link}
                to={`/architect/workflow-observer?definitionId=${workflowId}`}
              >
                Observe
              </Button>
            </Tooltip>
            {workflow.active && (
              <Button
                size="xs"
                leftSection={<IconRocket size={14} />}
                color="green"
                onClick={openLaunch}
              >
                Launch
              </Button>
            )}
          </Group>
        </Group>

        {/* Metadata Bar */}
        <Paper p="sm" withBorder>
          <Group gap="xl">
            <Group gap="xs">
              <IconGitBranch size={14} />
              <Text size="sm">
                {workflow.states.length} states, {workflow.transitions.length} transitions
              </Text>
            </Group>
            {workflow.timeoutSeconds && (
              <Group gap="xs">
                <IconClock size={14} />
                <Text size="sm">Timeout: {formatSeconds(workflow.timeoutSeconds)}</Text>
              </Group>
            )}
            {workflow.complianceLevel && (
              <Group gap="xs">
                <IconShield size={14} />
                <Text size="sm">Compliance: {workflow.complianceLevel}</Text>
              </Group>
            )}
          </Group>
        </Paper>

        {/* Selected Instance Banner */}
        {selectedInstance && (
          <Paper p="sm" withBorder style={{ borderColor: 'var(--mantine-color-orange-5)', backgroundColor: 'var(--mantine-color-orange-0)' }}>
            <Group justify="space-between">
              <Group gap="xs">
                <ThemeIcon size="sm" color="orange" variant="light">
                  <IconPlayerPlay size={12} />
                </ThemeIcon>
                <Text size="sm" fw={500}>
                  Viewing: {selectedInstance.instanceName ?? selectedInstance.id}
                </Text>
                <Badge size="sm" variant="light" color={INSTANCE_STATUS_COLORS[selectedInstance.status] ?? 'gray'}>
                  {selectedInstance.status}
                </Badge>
                <Text size="sm" c="dimmed">→ {formatStateName(selectedInstance.currentState)}</Text>
              </Group>
              <Group gap="xs">
                <Anchor
                  component={Link}
                  to={`/ferp-core/instances/${selectedInstance.id}`}
                  size="sm"
                >
                  Open instance
                </Anchor>
                <ActionIcon size="sm" variant="subtle" onClick={() => setSelectedInstance(null)}>
                  <IconCircleX size={14} />
                </ActionIcon>
              </Group>
            </Group>
          </Paper>
        )}

        {/* Split layout: Canvas + Instances Panel */}
        <Box style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          {/* State Machine Diagram */}
          <Card withBorder padding={0} style={{ flex: 1, minWidth: 0 }}>
            <div style={{ height: '600px', position: 'relative' }}>
              <ReactFlowCanvas nodes={nodes} edges={edges} config={canvasConfig} />
            </div>
          </Card>

          {/* Instances Panel */}
          <Box style={{ width: instancesPanelOpen ? 280 : 32, flexShrink: 0, transition: 'width 0.2s' }}>
            <Paper withBorder style={{ height: '600px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {/* Panel Header */}
              <Group p="xs" justify="space-between" style={{ borderBottom: '1px solid var(--mantine-color-default-border)', flexShrink: 0 }}>
                {instancesPanelOpen && (
                  <Group gap={4}>
                    <IconStack2 size={14} />
                    <Text size="xs" fw={600}>Running Instances</Text>
                  </Group>
                )}
                <Tooltip label={instancesPanelOpen ? 'Collapse panel' : 'Show instances'}>
                  <ActionIcon
                    size="xs"
                    variant="subtle"
                    onClick={() => setInstancesPanelOpen((v) => !v)}
                    style={{ marginLeft: instancesPanelOpen ? undefined : 'auto' }}
                  >
                    {instancesPanelOpen ? <IconChevronRight size={12} /> : <IconChevronLeft size={12} />}
                  </ActionIcon>
                </Tooltip>
              </Group>

              {instancesPanelOpen && workflowId && tenantId && (
                <ScrollArea style={{ flex: 1 }} p="xs">
                  <InstancesPanel
                    workflowId={workflowId}
                    tenantId={tenantId}
                    selectedInstanceId={selectedInstance?.id ?? null}
                    onInstanceSelect={(instance) => {
                      setSelectedInstance((prev) =>
                        prev?.id === instance.id ? null : instance,
                      );
                    }}
                  />
                </ScrollArea>
              )}
            </Paper>
          </Box>
        </Box>

        {/* Legend */}
        <Paper p="sm" withBorder>
          <Group gap="lg">
            <Text size="sm" fw={500}>
              Legend:
            </Text>
            {Object.entries(STATE_TYPE_COLORS).map(([type, color]) => (
              <Group key={type} gap={4}>
                <div
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 3,
                    backgroundColor: color,
                  }}
                />
                <Text size="xs">{STATE_TYPE_LABELS[type] ?? type}</Text>
              </Group>
            ))}
            <Group gap={4}>
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 3,
                  backgroundColor: '#ef4444',
                }}
              />
              <Text size="xs">Terminal</Text>
            </Group>
            <Group gap={4}>
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 3,
                  backgroundColor: '#f97316',
                  boxShadow: '0 0 0 2px #f97316',
                }}
              />
              <Text size="xs">Current State</Text>
            </Group>
          </Group>
        </Paper>

        {/* DRR-318: Workflow Orchestration Graph */}
        <Paper p="sm" withBorder>
          <Group
            justify="space-between"
            style={{ cursor: 'pointer' }}
            onClick={() => setOrchestrationOpen((v) => !v)}
          >
            <Group gap="xs">
              <IconRoute size={16} />
              <Text size="sm" fw={500}>Workflow Orchestration</Text>
              <Badge size="xs" variant="light" color="gray">DRR-318</Badge>
            </Group>
            <ActionIcon variant="subtle" size="sm">
              <IconChevronDown
                size={14}
                style={{
                  transform: orchestrationOpen ? 'rotate(180deg)' : undefined,
                  transition: 'transform 0.2s',
                }}
              />
            </ActionIcon>
          </Group>
          <Collapse in={orchestrationOpen}>
            <Box mt="md">
              <OrchestrationGraph workflowId={workflowId ?? ''} workflowName={workflow?.name} />
            </Box>
          </Collapse>
        </Paper>
      </Stack>

      {/* Launch Instance Modal */}
      <Modal opened={launchOpened} onClose={closeLaunch} title="Launch Workflow Instance" size="sm">
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Start a new run of <strong>{workflow?.name}</strong>.
          </Text>
          {launchError && (
            <Alert color="red" title="Launch failed">
              {launchError}
            </Alert>
          )}
          <TextInput
            label="Instance Name"
            description="Optional — leave blank for auto-generated name"
            placeholder="e.g. Q1 Review, Patient #1234..."
            value={launchName}
            onChange={(e) => setLaunchName(e.currentTarget.value)}
          />
          <Switch
            label="Start immediately after creation"
            description="If off, instance is created in Pending state and must be started manually"
            checked={autoStart}
            onChange={(e) => setAutoStart(e.currentTarget.checked)}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={closeLaunch} disabled={launchLoading}>
              Cancel
            </Button>
            <Button
              color="green"
              leftSection={<IconRocket size={14} />}
              loading={launchLoading}
              onClick={handleLaunch}
            >
              {autoStart ? 'Launch & Start' : 'Create Instance'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* State Detail Drawer */}
      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        title="State Details"
        position="right"
        size="lg"
      >
        {selectedState && (
          <Stack gap="md">
            {/* State Header */}
            <Card withBorder>
              <Stack gap="xs">
                <Title order={4}>{formatStateName(selectedState.name)}</Title>
                <Group gap="xs">
                  <Badge
                    color={STATE_TYPE_COLORS[selectedState.stateType]?.replace('#', '') ?? 'gray'}
                    variant="light"
                  >
                    {STATE_TYPE_LABELS[selectedState.stateType] ?? selectedState.stateType}
                  </Badge>
                  {selectedState.timeoutSeconds && (
                    <Badge variant="outline" leftSection={<IconClock size={12} />}>
                      {formatSeconds(selectedState.timeoutSeconds)}
                    </Badge>
                  )}
                </Group>
              </Stack>
            </Card>

            {/* Policy Gates (DRR-129) */}
            {selectedState.policyRefs.length > 0 && (
              <>
                <Divider label="Policy Gates (DRR-129)" labelPosition="left" />
                <Stack gap="xs">
                  {selectedState.policyRefs.map((ref) => (
                    <Paper key={ref} p="xs" withBorder>
                      <Group gap="xs">
                        <ThemeIcon size="sm" variant="light" color="green">
                          <IconShield size={12} />
                        </ThemeIcon>
                        <Text size="sm" fw={500}>{ref}</Text>
                      </Group>
                      <Text size="xs" c="dimmed" mt={2}>
                        Evaluated before entry actions. CRITICAL=block, WARNING=log, DEFERRED=audit.
                      </Text>
                    </Paper>
                  ))}
                </Stack>
              </>
            )}

            {/* Entry Actions */}
            {selectedState.entryActions.length > 0 && (
              <>
                <Divider label="Entry Actions" labelPosition="left" />
                <List size="sm" spacing="xs">
                  {selectedState.entryActions.map((action) => (
                    <List.Item key={action}>{action}</List.Item>
                  ))}
                </List>
              </>
            )}

            {/* Exit Actions */}
            {selectedState.exitActions.length > 0 && (
              <>
                <Divider label="Exit Actions" labelPosition="left" />
                <List size="sm" spacing="xs">
                  {selectedState.exitActions.map((action) => (
                    <List.Item key={action}>{action}</List.Item>
                  ))}
                </List>
              </>
            )}

            {/* Incoming Transitions */}
            {selectedState.incomingTransitions.length > 0 && (
              <>
                <Divider label="Incoming Transitions" labelPosition="left" />
                <Stack gap="xs">
                  {selectedState.incomingTransitions.map((t) => (
                    <Paper key={`${t.fromState}-${t.event}`} p="xs" withBorder>
                      <Group justify="space-between">
                        <Text size="sm">
                          From: <strong>{formatStateName(t.fromState)}</strong>
                        </Text>
                        <Badge size="sm" variant="outline">
                          {t.event}
                        </Badge>
                      </Group>
                      {t.guards.length > 0 && (
                        <Text size="xs" c="dimmed" mt={4}>
                          Guards: {t.guards.join(', ')}
                        </Text>
                      )}
                    </Paper>
                  ))}
                </Stack>
              </>
            )}

            {/* Outgoing Transitions */}
            {selectedState.outgoingTransitions.length > 0 && (
              <>
                <Divider label="Outgoing Transitions" labelPosition="left" />
                <Stack gap="xs">
                  {selectedState.outgoingTransitions.map((t) => (
                    <Paper key={`${t.toState}-${t.event}`} p="xs" withBorder>
                      <Group justify="space-between">
                        <Text size="sm">
                          To: <strong>{formatStateName(t.toState)}</strong>
                        </Text>
                        <Badge size="sm" variant="outline">
                          {t.event}
                        </Badge>
                      </Group>
                      {t.guards.length > 0 && (
                        <Text size="xs" c="dimmed" mt={4}>
                          Guards: {t.guards.join(', ')}
                        </Text>
                      )}
                      {t.actions.length > 0 && (
                        <Text size="xs" c="dimmed">
                          Actions: {t.actions.join(', ')}
                        </Text>
                      )}
                    </Paper>
                  ))}
                </Stack>
              </>
            )}
          </Stack>
        )}
      </Drawer>
    </Container>
  );
}

export default WorkflowDetailPage;
